import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import QrScanner from "react-qr-scanner";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function ScanAset() {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(true);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [currentCamera, setCurrentCamera] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Deteksi apakah aplikasi dijalankan di mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      setIsMobile(isMobileDevice);
    };

    checkMobile();
  }, []);

  // Dapatkan daftar kamera yang tersedia
  useEffect(() => {
    const getCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );
        setCameras(videoDevices);

        // Set kamera belakang sebagai default jika tersedia
        const backCamera = videoDevices.find(
          (device) =>
            device.label.toLowerCase().includes("back") ||
            device.label.toLowerCase().includes("belakang") ||
            device.label.toLowerCase().includes("rear")
        );

        if (backCamera) {
          const backCameraIndex = videoDevices.findIndex(
            (device) => device.deviceId === backCamera.deviceId
          );
          setCurrentCamera(backCameraIndex);
        }
      } catch (err) {
        console.error("Error getting cameras:", err);
      }
    };

    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      getCameras();
    }
  }, []);

  const handleScan = (result) => {
    if (result) {
      try {
        // Parse URL dari hasil scan
        const url = new URL(result.text);
        // Ambil ID dari path URL
        const id = url.pathname.split("/").pop();
        // Navigate ke halaman detail aset
        navigate(`/pages/aset/detail/${id}`);
      } catch (err) {
        setError("QR Code tidak valid");
        setScanning(false);
      }
    }
  };

  const handleError = (err) => {
    setError("Terjadi kesalahan saat memindai QR Code");
    console.error(err);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRetry = () => {
    setError(null);
    setScanning(true);
  };

  // Fungsi untuk mengganti kamera
  const switchCamera = () => {
    if (cameras.length > 1) {
      setCurrentCamera((prev) => (prev + 1) % cameras.length);
    }
  };

  // Konfigurasi kamera untuk QrScanner
  const constraints = {
    video: {
      deviceId: cameras[currentCamera]?.deviceId
        ? { exact: cameras[currentCamera].deviceId }
        : undefined,
      facingMode: currentCamera === 0 ? "environment" : "user",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Card>
          <MDBox p={3}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <MDTypography variant="h4">Scan QR Code Aset</MDTypography>
              <MDButton variant="gradient" color="info" onClick={handleBack}>
                <Icon>arrow_back</Icon>&nbsp;Kembali
              </MDButton>
            </MDBox>

            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              p={3}
            >
              {error ? (
                <MDBox textAlign="center">
                  <MDTypography variant="h6" color="error" mb={2}>
                    {error}
                  </MDTypography>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleRetry}
                  >
                    <Icon>refresh</Icon>&nbsp;Coba Lagi
                  </MDButton>
                </MDBox>
              ) : (
                <>
                  <MDTypography variant="h6" mb={2}>
                    Arahkan kamera ke QR Code aset
                  </MDTypography>

                  {/* Tombol ganti kamera - tampil jika ada lebih dari 1 kamera */}
                  {cameras.length > 1 && (
                    <MDBox mb={2}>
                      <MDButton
                        variant="outlined"
                        color="info"
                        onClick={switchCamera}
                        startIcon={<Icon>flip_camera_ios</Icon>}
                      >
                        Ganti Kamera ({currentCamera + 1}/{cameras.length})
                      </MDButton>
                    </MDBox>
                  )}

                  <MDBox
                    sx={{
                      width: "100%",
                      maxWidth: 400,
                      height: 400,
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "8px",
                    }}
                  >
                    <QrScanner
                      delay={300}
                      onError={handleError}
                      onScan={handleScan}
                      style={{ width: "100%", height: "100%" }}
                      constraints={constraints}
                      key={currentCamera} // Force re-render when camera changes
                    />
                  </MDBox>

                  <MDTypography variant="caption" color="text" mt={2}>
                    Pastikan QR Code berada dalam area pemindaian
                  </MDTypography>

                  {/* Informasi kamera yang sedang digunakan - tampil di semua perangkat */}
                  {cameras.length > 0 && (
                    <MDTypography variant="caption" color="text" mt={1}>
                      Kamera:{" "}
                      {cameras[currentCamera]?.label ||
                        `Kamera ${currentCamera + 1}`}
                    </MDTypography>
                  )}
                </>
              )}
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ScanAset;
